import { useState } from "react";
import { Button } from "../../components/ui/button";
import { FileText, Mail, Pencil, Trash2 } from "lucide-react";
import EditApplicationModal from "./EditApplicationModal";

const API = process.env.REACT_APP_BACKEND_URL;

const statusBadgeClass = (status) => {
  switch (status) {
    case "pending":
      return "bg-yellow-500/20 text-yellow-400";
    case "approved":
      return "bg-[#047857]/20 text-[#047857]";
    case "draft":
      return "bg-blue-500/20 text-blue-300";
    case "rejected":
      return "bg-red-500/20 text-red-400";
    default:
      return "bg-white/10 text-white/60";
  }
};

const ApplicationsTab = ({
  businessApplications,
  subLocations = [],
  onApprove,
  onReject,
  onDelete,
  onEmailContract,
  onSaved,
  getAuthHeader,
}) => {
  const [editingApp, setEditingApp] = useState(null);

  const handleDelete = (app) => {
    if (!window.confirm(`Delete the ${app.status === "draft" ? "draft" : "application"} for "${app.business_name || app.email || "this entry"}"?`)) return;
    onDelete?.(app.id);
  };

  return (
    <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
      <div className="p-4 border-b border-[#D4AF37]/10">
        <h3 className="font-['Playfair_Display'] text-xl text-white">Business Applications</h3>
        <p className="text-white/40 text-xs mt-1">
          Drafts come from venues that saved a partial signup. Click <span className="text-white/70">Edit</span> to complete and submit for review.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="applications-table">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Business</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Contact</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Category</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Discount</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Agreement</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Status</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {businessApplications.map((app) => (
              <tr key={app.id} className="border-t border-white/5">
                <td className="p-4">
                  <p className="text-white font-medium">{app.business_name || <span className="text-white/30 italic">(no name yet)</span>}</p>
                  <p className="text-white/40 text-xs mt-1">{app.address || <span className="italic">no address yet</span>}</p>
                </td>
                <td className="p-4">
                  <p className="text-white">{app.name || <span className="text-white/30 italic">no contact</span>}</p>
                  <p className="text-white/40 text-xs">{app.email}</p>
                </td>
                <td className="p-4 text-white/70 capitalize">{app.category || <span className="text-white/30">—</span>}</td>
                <td className="p-4 text-[#047857] font-bold">{app.discount_level ? `${app.discount_level}%` : <span className="text-white/30">—</span>}</td>
                <td className="p-4">
                  {app.signed_agreement_url ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={`${API}${app.signed_agreement_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#D4AF37] hover:underline text-xs flex items-center gap-1"
                      >
                        <FileText className="w-3 h-3" />
                        View
                      </a>
                      <button
                        onClick={() => onEmailContract(app)}
                        className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                        title="Email contract to business"
                      >
                        <Mail className="w-3 h-3" />
                        Send
                      </button>
                    </div>
                  ) : (
                    <span className="text-white/30 text-xs">No agreement</span>
                  )}
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusBadgeClass(app.status)}`}>
                    {app.status}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {(app.status === "draft" || app.status === "pending") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingApp(app)}
                        className="text-white/70 hover:text-white text-xs px-2 py-1 border border-white/10"
                        title={app.status === "draft" ? "Complete this draft" : "Edit application"}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    )}
                    {app.status === "pending" && (
                      <Button
                        size="sm"
                        onClick={() => onApprove(app.id)}
                        className="bg-[#047857] hover:bg-[#047857]/80 text-white text-xs px-3 py-1"
                      >
                        Approve
                      </Button>
                    )}
                    {app.status === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onReject(app.id)}
                        className="text-red-400 hover:text-red-300 text-xs"
                      >
                        Reject
                      </Button>
                    )}
                    {(app.status === "draft" || app.status === "rejected") && onDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(app)}
                        className="text-red-400 hover:text-red-300 text-xs"
                        title="Delete this entry permanently"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {businessApplications.length === 0 && (
          <p className="p-8 text-center text-white/40">No applications yet</p>
        )}
      </div>

      {editingApp && (
        <EditApplicationModal
          application={editingApp}
          subLocations={subLocations}
          apiBase={`${API}/api`}
          getAuthHeader={getAuthHeader}
          onClose={() => setEditingApp(null)}
          onSaved={(newStatus) => onSaved?.(newStatus)}
        />
      )}
    </div>
  );
};

export default ApplicationsTab;
